import { useSelector } from 'react-redux';
import { useFacade } from './useFacade';
import { Loader, Chart } from 'components';
import { StatisticBtn } from './components/statisticBtn/StatisticBtn';
import { ChartError } from './components/ChartError';

import { Palette } from 'config';
import * as S from './ReportsChart.styled';

const colors = {
  installs: {
    main: Palette.Fuchsia._500,
    background: Palette.Fuchsia._100,
  },
  impressions: {
    main: Palette.LightBlue._500,
    background: Palette.LightBlue._100,
  },
  clicks: {
    main: Palette.Violet._500,
    background: Palette.Violet._100,
  },
  ctr: {
    main: Palette.Amber._500,
    background: Palette.Amber._100,
  },
  earning: {
    main: Palette.Pink._500,
    background: Palette.Pink._100,
  },
  eCPM: {
    main: Palette.Teal._500,
    background: Palette.Teal._100,
  },
};

// list all metrics to map over later in the code (order matters)
const metrics = ['impressions', 'clicks', 'installs', 'ctr', 'earning', 'eCPM'];

export const ReportsChart = () => {
  const timePeriod = useSelector((state) => state.reports.selectedDateRangeReports);

  const {
    isFetchingDataChart,
    isFetchingPrevDataChart,
    isError,
    dataPerDay,
    totalValues,
    numOfDays,
    change,
    errorMsg,
    selectedMetrics,
    isChartHidden,
  } = useFacade({ timePeriod, metrics });

  if (isFetchingDataChart) {
    return (
      <S.LoaderWrap>
        <Loader.Local delay={0} />
      </S.LoaderWrap>
    );
  }

  if (isError && !isFetchingDataChart) {
    return (
      <S.ErrorContainer>
        <ChartError message={errorMsg} />
      </S.ErrorContainer>
    );
  }

  if (!dataPerDay?.size && !isError) {
    return (
      <S.ErrorContainer>
        <ChartError message={'No Data'} />
      </S.ErrorContainer>
    );
  }

  return (
    !!dataPerDay?.size && (
      <S.Container>
        <S.StatisticBox>
          <StatisticBtn
            label='Impressions'
            avg={formatNumber(totalValues.impressions / numOfDays)}
            change={change.impressions}
            numOfDays={numOfDays}
            value={formatNumber(totalValues.impressions)}
            color={colors.impressions}
            metric='impressions'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='Clicks'
            avg={formatNumber(totalValues.clicks / numOfDays)}
            change={change.clicks}
            numOfDays={numOfDays}
            value={formatNumber(totalValues.clicks)}
            color={colors.clicks}
            metric='clicks'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='Installs'
            value={formatNumber(totalValues.installs)}
            avg={formatNumber(totalValues.installs / numOfDays)}
            change={change.installs}
            numOfDays={numOfDays}
            color={colors.installs}
            metric='installs'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='CTR'
            change={change.ctr}
            numOfDays={numOfDays}
            value={`${formatNumber(totalValues.ctr)}%`}
            color={colors.ctr}
            metric='ctr'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='Earning'
            avg={formatNumber(totalValues.earning / numOfDays)}
            change={change.earning}
            numOfDays={numOfDays}
            value={`$${formatNumber(totalValues.earning)}`}
            color={colors.earning}
            metric='earning'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
          <StatisticBtn
            label='eCPM'
            change={change.eCPM}
            numOfDays={numOfDays}
            value={`$${formatNumber(totalValues.eCPM)}`}
            color={colors.eCPM}
            metric='eCPM'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            selectedMetrics={selectedMetrics}
            isChartHidden={isChartHidden}
          />
        </S.StatisticBox>
        <Chart
          dataPerDay={dataPerDay}
          colors={colors}
          metrics={metrics}
          selectedMetrics={selectedMetrics}
          selectedMetricsSelector='reports.selectedMetricsReports'
          isChartHidden={isChartHidden}
        />
      </S.Container>
    )
  );
};

function formatNumber(number) {
  const suffixes = ['', 'K', 'M', 'B']; // thousands, millions, billions

  const suffixIndex = Math.floor(Math.log10(number) / 3);
  const scaledNumber = suffixes[suffixIndex] ? number / Math.pow(10, suffixIndex * 3) : number;
  let formattedNumber = scaledNumber?.toFixed(2);

  if (formattedNumber?.indexOf('.') !== -1) {
    formattedNumber = formattedNumber?.replace(/\.?0*$/, '');
  }

  return formattedNumber + (suffixes[suffixIndex] ? suffixes[suffixIndex] : '');
}
